import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '/blog/posts?limit=50&offset=0';
      if (selectedCategory) url += `&category=${selectedCategory}`;

      const [postsRes, categoriesRes] = await Promise.all([
        api.get(url),
        api.get('/blog/categories')
      ]);

      const allPosts = postsRes.data.posts || [];
      setPosts(allPosts);

      setFeaturedPosts(allPosts.slice(0, 5));
      setRecentPosts(allPosts.slice(5, 20));
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (slug) => {
    setSelectedCategory(slug === selectedCategory ? '' : slug);
    setCurrentSlide(0);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredPosts.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredPosts.length) % featuredPosts.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    return `https://graduates.katinetti.ac.ke${imageUrl}`;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const truncateText = (text, length = 120) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FEFBEC', minHeight: '100vh' }}>
      {/* Hero Section - Left Aligned with Gold Bottom Border - Reduced Height */}
      <div className="bg-white border-b-4 border-[#fbbf24] shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-xl">📰</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800 mb-0.5">
                Latest News & Updates
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Stay informed with the latest happenings at Katine TTI
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Featured Posts Carousel */}
          <div className="lg:w-2/3">
            {featuredPosts.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="relative h-80 sm:h-96 overflow-hidden">
                  {getImageUrl(featuredPosts[currentSlide]?.featured_image) ? (
                    <img
                      src={getImageUrl(featuredPosts[currentSlide]?.featured_image)}
                      alt={featuredPosts[currentSlide]?.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <span className="text-white text-6xl">📰</span>
                    </div>
                  )}

                  {featuredPosts[currentSlide]?.category_name && (
                    <span className="absolute top-4 left-4 px-3 py-1 bg-[#fbbf24] text-gray-900 text-xs font-semibold rounded-full">
                      {featuredPosts[currentSlide]?.category_name}
                    </span>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span>{formatDate(featuredPosts[currentSlide]?.published_at)}</span>
                    <span>•</span>
                    <span>👁️ {featuredPosts[currentSlide]?.view_count || 0} views</span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
                    {featuredPosts[currentSlide]?.title}
                  </h2>
                  <p className="text-gray-600 mb-4">
                    {featuredPosts[currentSlide]?.excerpt || truncateText(featuredPosts[currentSlide]?.content, 150)}
                  </p>
                  <Link
                    to={`/blog/${featuredPosts[currentSlide]?.slug}`}
                    className="inline-flex items-center gap-2 text-[#fbbf24] hover:text-[#e6a812] font-semibold transition-colors"
                  >
                    Read More →
                  </Link>
                </div>

                {featuredPosts.length > 1 && (
                  <div className="flex justify-center gap-2 pb-6">
                    {featuredPosts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`h-2 rounded-full transition-all ${
                          currentSlide === index
                            ? 'w-8 bg-[#fbbf24]'
                            : 'w-2 bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Recent Posts List */}
          <div className="lg:w-1/3">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              <div className="flex items-center justify-between mb-4 pb-3 border-b">
                <h3 className="text-lg font-bold text-gray-800">Recent Articles</h3>
                <span className="text-xs text-gray-500">Scroll to view more ↓</span>
              </div>

              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {recentPosts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No more articles found.</p>
                ) : (
                  recentPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="block group"
                    >
                      <div className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                          {getImageUrl(post.featured_image) ? (
                            <img
                              src={getImageUrl(post.featured_image)}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                              <span className="text-white text-2xl">📄</span>
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800 text-sm line-clamp-2 group-hover:text-primary transition-colors">
                            {post.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            {post.category_name && (
                              <span className="text-xs text-[#fbbf24] font-medium">
                                {post.category_name}
                              </span>
                            )}
                            <span className="text-xs text-gray-400">
                              {new Date(post.published_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {truncateText(post.content, 60)}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>

              <div className="mt-4 pt-3 border-t text-center">
                <Link
                  to="/blog/archive"
                  className="text-sm text-[#fbbf24] hover:text-[#e6a812] font-medium"
                >
                  View All Articles →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Category Sections */}
        {categories.slice(0, 4).map((category) => {
          const categoryPosts = posts.filter(p => p.category_id === category.id).slice(0, 3);
          if (categoryPosts.length === 0) return null;

          return (
            <div key={category.id} className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800 border-l-4 border-[#fbbf24] pl-4">
                  {category.name}
                </h2>
                <Link
                  to={`/blog?category=${category.slug}`}
                  className="text-sm text-[#fbbf24] hover:text-[#e6a812]"
                >
                  View All →
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {categoryPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="h-48 overflow-hidden">
                      {getImageUrl(post.featured_image) ? (
                        <img
                          src={getImageUrl(post.featured_image)}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <span className="text-white text-4xl">📰</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {truncateText(post.content, 80)}
                      </p>
                      <div className="mt-3 text-[#fbbf24] text-sm font-medium group-hover:underline">
                        Read More →
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #fbbf24;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #e6a812;
        }
      `}</style>
    </div>
  );
};

export default BlogPage;
